import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {projectTitleInitialState} from '../reducers/project-title';
import {showAlertWithTimeout} from '../reducers/alerts';
import Spinner from '../components/spinner/spinner.jsx';
import { forEach } from 'jszip';

/**
 * Cloud uploader component passes an uploadToCloud function to its child.
 * It expects this child to be a function with the signature
 *     function (uploadToCloud, props) {}
 * The component can then be used to attach cloud upload functionality
 * to any other component:
 *
 * <CloudUploader>{(uploadToCloud, props) => (
 *     <MyCoolComponent
 *         onClick={uploadToCloud}
 *         {...props}
 *     />
 * )}</CloudUploader>
 */
class CloudUploader extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'uploadToCloud'
        ]);
        this.state = {
            isUploading: false
        };
    }

    uploadToCloud () {
        // Set uploading state and show loading alert
        this.setState({isUploading: true});
        this.props.onShowUploadingAlert();

        // Generate the .ob file content
        this.props.saveProjectSb3().then(content => {
            if (this.props.onSaveFinished) {
                this.props.onSaveFinished();
            }

            // Create blob from the content
            const blob = new Blob([content], {type: 'application/octet-stream'});
            const file = new File([blob], this.props.projectFilename, {type: 'application/octet-stream'});

            // Upload to cloud
            this.uploadFileToCloud(file);
        })
            .catch(() => {
                this.props.onShowUploadErrorAlert();
                this.setState({isUploading: false});
            });
    }

    uploadFileToCloud (file) {
        const formData = new FormData();
        formData.append('file', file);
        const boardConfig = 'esp32:esp32:esp32s3:FlashSize=16M,PSRAM=opi,PartitionScheme=app3M_fat9M_16MB,CDCOnBoot=cdc,USBMode=hwcdc';
        formData.append('board', boardConfig);
        
        fetch('https://robo-api.windify.edu.vn/api/files/upload-ob', {
            method: 'POST',
            body: formData
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                // Check if upload was successful
                if (data.success && data.downloadUrl) {
                    this.props.onShowUploadSuccessAlert({fileUrl: data.downloadUrl});
                } else {
                    throw new Error(data.error || 'Upload failed');
                }
                this.setState({isUploading: false});
            })
            .catch(() => {
                this.props.onShowUploadErrorAlert();
                this.setState({isUploading: false});
            });
    }

    render () {
        const {
            children
        } = this.props;
        const overlayStyle = {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.35)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        };
        const overlayContentStyle = {
            background: 'rgba(0, 0, 0, 0.0)',
            padding: '1rem',
            borderRadius: '.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        };
        return (
            <React.Fragment>
                {children(
                    this.props.className,
                    this.uploadToCloud
                )}
                {this.state.isUploading ? (
                    <div style={overlayStyle}>
                        <div style={overlayContentStyle}>
                            <Spinner
                                large
                                level="primary"
                            />
                        </div>
                    </div>
                ) : null}
            </React.Fragment>
        );
    }
}

const getProjectFilename = (curTitle, defaultTitle) => {
    let filenameTitle = curTitle;
    if (!filenameTitle || filenameTitle.length === 0) {
        filenameTitle = defaultTitle;
    }
    return `${filenameTitle.substring(0, 100)}.ob`;
};

CloudUploader.propTypes = {
    children: PropTypes.func,
    className: PropTypes.string,
    onSaveFinished: PropTypes.func,
    projectFilename: PropTypes.string,
    saveProjectSb3: PropTypes.func,
    onShowUploadingAlert: PropTypes.func,
    onShowUploadSuccessAlert: PropTypes.func,
    onShowUploadErrorAlert: PropTypes.func
};

CloudUploader.defaultProps = {
    className: ''
};

const mapStateToProps = state => ({
    saveProjectSb3: state.scratchGui.vm.saveProjectSb3.bind(state.scratchGui.vm),
    projectFilename: getProjectFilename(
        state.scratchGui.projectTitle || projectTitleInitialState,
        'Untitled'
    )
});

const mapDispatchToProps = dispatch => ({
    onShowUploadingAlert: () => showAlertWithTimeout(dispatch, 'uploadingToCloud'),
    onShowUploadSuccessAlert: data => {
        // Dispatch alert with data
        dispatch({
            type: 'scratch-gui/alerts/SHOW_ALERT',
            alertId: 'uploadToCloudSuccess',
            data: data,
            fileUrl: data.fileUrl
        });
    },
    onShowUploadErrorAlert: () => showAlertWithTimeout(dispatch, 'uploadToCloudError')
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(CloudUploader);